import { create } from "zustand";
import { authStorage, storage } from "../utils/storage";
import { SOCKET_EVENTS } from "../utils/constants";
import { getSocket, encode, decode, connectSocket } from "../services/socket";
import Cookies from "js-cookie";

export const useAuthStore = create((set, get) => ({
  user: authStorage.isLoggedIn() ? {
    uid: localStorage.getItem("uid") || null,
    id: localStorage.getItem("uid") || null,
    name: storage.getKey("name") || null,
    email: storage.getKey("email") || null,
    avatar: storage.getKey("avatar") || null,
    credit: storage.getKey("credit") || 0,
  } : null,
  isAuthenticated: authStorage.isLoggedIn(),
  isLoading: false,
  error: null,
  showTwoFactor: false,
  tempLoginData: null,
  _regPassword: null, // temp store password for auto-login after register

  // Login via socket (exact old frontend pattern)
  login: (username, password) => {
    set({ isLoading: true, error: null });
    const s = getSocket() || connectSocket();

    // Register listener BEFORE emit
    s.off(SOCKET_EVENTS.LOGIN_USER);
    s.on(SOCKET_EVENTS.LOGIN_USER, (data) => {
      const decoded = decode(data);
      if (!decoded) return;
      get()._handleLoginResponse(decoded);
    });

    s.emit(SOCKET_EVENTS.LOGIN_USER, encode({
      username,
      password,
      recaptcha: "google",
    }));
  },

  // Register via socket (exact old frontend RegisterPage.js pattern)
  register: (username, email, password, referralCode, country, phone) => {
    set({ isLoading: true, error: null, _regPassword: password });
    const s = getSocket() || connectSocket();

    // Register listener BEFORE emit
    s.off(SOCKET_EVENTS.REGISTER_USER);
    s.on(SOCKET_EVENTS.REGISTER_USER, (data) => {
      const decoded = decode(data);
      if (!decoded) return;
      get()._handleRegisterResponse(decoded);
    });

    // Exact payload from old frontend RegisterPage.js finalizeRegistration()
    s.emit(SOCKET_EVENTS.REGISTER_USER, encode({
      username: username,
      password: password,
      email: email,
      phone: phone || "",
      method: true,
      refree: referralCode || "",
      country: country || "",
      aff: storage.getKey("aff") || referralCode || null,
    }));
  },

  // Google login via socket
  loginWithGoogle: (username, email, token) => {
    set({ isLoading: true, error: null });
    const s = getSocket() || connectSocket();

    s.off(SOCKET_EVENTS.LOGIN_USER_GOOGLE);
    s.on(SOCKET_EVENTS.LOGIN_USER_GOOGLE, (data) => {
      const decoded = decode(data);
      if (!decoded) return;
      get()._handleLoginResponse(decoded);
    });

    s.emit(SOCKET_EVENTS.LOGIN_USER_GOOGLE, encode({
      username,
      email,
      token,
    }));
  },

  // Forgot password via socket
  resetPassword: (email, onSuccess, onError) => {
    set({ isLoading: true, error: null, _onResetSuccess: onSuccess, _onResetError: onError });
    const s = getSocket() || connectSocket();

    s.off(SOCKET_EVENTS.RESET_PASSWORD);
    s.on(SOCKET_EVENTS.RESET_PASSWORD, (data) => {
      const decoded = decode(data);
      if (!decoded) return;
      set({ isLoading: false });
      if (decoded.status) {
        get()._onResetSuccess?.();
      } else {
        const msg = "This email is not registered in our system";
        set({ error: msg });
        get()._onResetError?.(msg);
      }
    });

    s.emit(SOCKET_EVENTS.RESET_PASSWORD, encode({ email }));
  },

  // 2FA verify
  verifyTwoFactor: async (uid, code) => {
    set({ isLoading: true });
    try {
      const response = await fetch("https://api.air1bet.com/2fa/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid, token: code }),
      });
      const data = await response.json();
      if (data.success) {
        get()._setLogin({ ...get().tempLoginData, status: true });
      } else {
        set({ error: "Invalid verification code", isLoading: false });
      }
    } catch {
      set({ error: "Failed to verify code", isLoading: false });
    }
  },

  // Internal: handle login response
  _handleLoginResponse: async (data) => {
    if (data.status === true) {
      try {
        const res = await fetch(`https://api.air1bet.com/2fa/status/${data.uid}`);
        const twoFaData = await res.json();
        if (twoFaData.isEnabled) {
          set({ showTwoFactor: true, tempLoginData: data, isLoading: false });
          return;
        }
      } catch {
        // 2FA check failed, proceed with login
      }
      get()._setLogin(data);
    } else {
      set({
        error: typeof data.status === "string" ? data.status : "Login failed",
        isLoading: false,
      });
    }
  },

  // Internal: handle register response (exact old frontend RegisterPage.js setRegister pattern)
  _handleRegisterResponse: (data) => {
    // Error from server
    if (data?.error) {
      set({ error: data.error, isLoading: false, _regPassword: null });
      return;
    }

    // Success — auto-login (exact old frontend pattern: emit LOGIN_USER with data.name + stored password)
    if (data?.status) {
      set({ isLoading: false });
      const password = get()._regPassword;
      const s = getSocket();
      if (s && password) {
        // Listen for login response (once, so it doesn't stack)
        s.off(SOCKET_EVENTS.LOGIN_USER);
        s.on(SOCKET_EVENTS.LOGIN_USER, (raw) => {
          const decoded = decode(raw);
          if (!decoded) return;
          if (decoded.status === true) {
            get()._setLogin(decoded);
          } else {
            set({ isLoading: false, _regPassword: null });
          }
        });

        // Auto-login after 500ms (matches old frontend wait)
        setTimeout(() => {
          s.emit(SOCKET_EVENTS.LOGIN_USER, encode({
            username: data.name,
            password: password,
            recaptcha: "google",
          }));
        }, 500);
      }
      set({ _regPassword: null });
      return;
    }

    set({ error: "Registration failed", isLoading: false, _regPassword: null });
  },

  // Internal: store login data and reload (exact old frontend setLogin pattern)
  _setLogin: (data) => {
    Cookies.set("session", data.token, { expires: 14 });
    Cookies.set("uid", data.uid);
    localStorage.setItem("uid", data.uid);

    storage.setKey("logged", true);
    storage.setKey("token", data.token);
    storage.setKey("name", data.name);
    storage.setKey("avatar", data.avatar);
    storage.setKey("email", data.email);
    storage.setKey("room", data.room);
    storage.setKey("friends", data.friends);
    storage.setKey("country", data.country);

    let creditValue;
    if (typeof data.credit === "object" && data.credit !== null) {
      creditValue = data.credit.inr || data.credit.INR || 0;
    } else {
      creditValue = data.credit || 0;
    }
    storage.setKey("credit", creditValue);
    localStorage.setItem("showLoginSuccessModal", "true");
    localStorage.setItem("coinRefresh", "true");

    authStorage.setToken(data.token);
    authStorage.setLoggedIn(true);
    authStorage.setUserId(data.uid);

    set({
      user: { id: data.uid, uid: data.uid, name: data.name, email: data.email, avatar: data.avatar, credit: creditValue },
      isAuthenticated: true,
      isLoading: false,
      showTwoFactor: false,
      tempLoginData: null,
    });

    setTimeout(() => { window.location.reload(); }, 500);
  },

  logout: () => {
    Cookies.remove("session");
    Cookies.remove("uid");
    localStorage.removeItem("uid");
    authStorage.removeToken();
    authStorage.setLoggedIn(false);
    storage.removeKey("logged");
    storage.removeKey("token");
    storage.removeKey("name");
    storage.removeKey("avatar");
    storage.removeKey("email");
    storage.removeKey("credit");
    storage.removeKey("room");
    storage.removeKey("friends");
    storage.removeKey("country");
    set({ user: null, isAuthenticated: false, showTwoFactor: false, tempLoginData: null });
  },

  setUser: (user) => set({ user }),
  checkAuth: () => set({ isAuthenticated: authStorage.isLoggedIn() }),
  clearError: () => set({ error: null }),
}));
